import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { generateWesWebhookSecret } from '@/lib/webhookSecret';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { inputClass, labelClass } from '@/components/ui/inputStyles';

type AndroidRobotApiKeyCardProps = {
  miniprogramId: number;
  /** compact：机器人管理页；full：带完整说明文案 */
  variant?: 'compact' | 'full';
  className?: string;
};

export function AndroidRobotApiKeyCard({
  miniprogramId,
  variant = 'compact',
  className = '',
}: AndroidRobotApiKeyCardProps) {
  const [loading, setLoading] = useState(true);
  const [androidKey, setAndroidKey] = useState('');
  const [keySaving, setKeySaving] = useState(false);
  const [keyMsg, setKeyMsg] = useState('');
  const [copyTip, setCopyTip] = useState('');

  const load = useCallback(async () => {
    if (miniprogramId < 1) {
      setAndroidKey('');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await apiFetch<{ ok: boolean; miniprogram?: { android_robot_api_key?: string } }>(
        'get_miniprogram',
        { method: 'GET', query: { id: miniprogramId } },
      );
      setAndroidKey(r.ok ? String(r.miniprogram?.android_robot_api_key ?? '').trim() : '');
    } finally {
      setLoading(false);
    }
  }, [miniprogramId]);

  useEffect(() => {
    void load();
  }, [load]);

  function fillRandomAndroidRobotKey() {
    setAndroidKey(generateWesWebhookSecret());
    setKeyMsg('已填入随机密钥，请点击「保存密钥」写入服务端');
  }

  async function saveAndroidKey() {
    if (miniprogramId < 1) return;
    setKeySaving(true);
    setKeyMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_miniprogram_android_robot_key', {
        method: 'POST',
        body: JSON.stringify({ miniprogram_id: miniprogramId, android_robot_api_key: androidKey }),
      });
      setKeyMsg(r.ok ? '已保存' : r.err || '保存失败');
      if (r.ok) await load();
    } finally {
      setKeySaving(false);
    }
  }

  async function copyKey() {
    const text = androidKey.trim();
    if (!text) {
      setCopyTip('尚未配置密钥');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopyTip('已复制');
    } catch {
      setCopyTip('复制失败，请手动选中复制');
    }
    window.setTimeout(() => setCopyTip(''), 2500);
  }

  if (miniprogramId < 1) return null;

  const isEmpty = !androidKey.trim();

  return (
    <Card
      title="Android 接口密钥"
      className={className}
      titleExtra={
        !loading && isEmpty ? (
          <span className="text-xs font-medium text-amber-800">未配置 · 机器人接口将拒绝鉴权</span>
        ) : null
      }
    >
      {variant === 'full' ? (
        <p className="text-sm text-slate-600">
          Android 端拉取转发任务、上报完成状态时需携带此密钥。留空则拒绝机器人接口请求；建议定期更换并同步到客户端「系统配置」。
        </p>
      ) : (
        <p className="text-sm text-slate-600">
          填入贴图虾 Android App「系统配置」中的<strong className="font-medium text-slate-800">接口密钥</strong>
          ；下方机器人列表中的 <span className="font-mono text-slate-800">#编号</span> 即为{' '}
          <strong className="font-medium text-slate-800">机器人 id</strong>。仍兼容「站点管理」中的站点级密钥（不推荐新装使用）。
        </p>
      )}

      {loading ? (
        <div className="mt-3">
          <Spinner label="加载密钥" />
        </div>
      ) : (
        <>
          {keyMsg ? <p className="mt-2 text-sm text-slate-700">{keyMsg}</p> : null}
          <div className="mt-3 flex max-w-2xl flex-col gap-2 sm:flex-row sm:items-end">
            <label className={`${labelClass} min-w-0 flex-1`}>
              <span>密钥（明文）</span>
              <input
                className={`${inputClass} font-mono text-sm`}
                value={androidKey}
                onChange={(e) => setAndroidKey(e.target.value)}
                placeholder="留空表示未配置"
                spellCheck={false}
                autoComplete="off"
                maxLength={255}
                disabled={keySaving}
              />
            </label>
            <div className="flex shrink-0 flex-wrap gap-2 sm:mb-0.5">
              <Button type="button" variant="secondary" disabled={keySaving || isEmpty} onClick={() => void copyKey()}>
                复制
              </Button>
              <Button type="button" variant="secondary" disabled={keySaving} onClick={fillRandomAndroidRobotKey}>
                生成随机
              </Button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button type="button" variant="primary" disabled={keySaving} onClick={() => void saveAndroidKey()}>
              {keySaving ? '保存中…' : '保存密钥'}
            </Button>
            {copyTip ? <span className="text-sm text-slate-600">{copyTip}</span> : null}
          </div>
        </>
      )}
    </Card>
  );
}
